/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {
    Component, ElementRef, HostBinding,
    HostListener, Input, OnDestroy, OnInit
} from '@angular/core';
import {EditorView} from 'prosemirror-view';
import {Subscription} from 'rxjs';
import {FcAgEditorService} from '../../../editor.service';
import {MenuService} from '../menu.service';
import {ToggleCommands} from '../MenuCommands';

/**
 * Dropdown component.
 */
@Component({
    selector: 'fc-ag-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss']
})
export class DropdownComponent implements OnInit, OnDestroy {
    private editorView: EditorView;

    private updateSubscription: Subscription;

    /**
     * Group.
     */
    @Input() group: string;

    /**
     * Items.
     */
    @Input() items: any[] | undefined;

    /**
     * Is dropdown open.
     */
    isDropdownOpen = false;

    private activeItems: any[] = [];

    /**
     * Disabled items.
     */
    disabledItems: string[] = [];

    /**
     * Active Item.
     */
    activeItem: string | null;

    /**
     * Default constructor.
     * 
     * @param fcAgEditorService FcAgEditorService.
     * @param menuService Menu service.
     * @param el Element Reference.
     */
    constructor(
        private fcAgEditorService: FcAgEditorService,
        private menuService: MenuService,
        private el: ElementRef
    ) {
    }

    /**
     * Is selected binding.
     * 
     * @returns Boolean.
     */
    @HostBinding('class.FcAgEditor__Dropdown--Selected') get isSelected(): boolean {
        return Boolean(this.activeItem || this.isDropdownOpen);
    }

    /**
     * Is dropdown disabled.
     * 
     * @returns Boolean.
     */
    @HostBinding('class.FcAgEditor--Disabled') get isDropdownDisabled(): boolean {
        if (this.items) {
            return this.disabledItems.length === this.items.length;
        }

        return true;
    }

    /**
     * On document click.
     * 
     * @param target Node.
     */
    @HostListener('document:mousedown', ['$event.target']) onDocumentClick(target: Node): void {
        if (!this.el.nativeElement.contains(target) && this.isDropdownOpen) {
            this.isDropdownOpen = false;
        }
    }

    /**
     * Get name.
     * 
     * @param key String.
     * @returns String.
     */
    getName(key: string): string {
        return this.fcAgEditorService.locals.get(key);
    }

    /**
     * Toggle dropdown.
     * 
     * @param e Mouse event.
     */
    toggleDropdown(e: MouseEvent): void {
        e.preventDefault();
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    /**
     * Track by index.
     * 
     * @param index Number.
     * @returns Number.
     */
    trackByIndex(index: number): number {
        return index;
    }

    /**
     * On click.
     * 
     * @param e Mouse event.
     * @param item Item.
     */
    onClick(e: MouseEvent, item: any): void {
        e.preventDefault();

        // consider only left click
        if (e.button !== 0) {
            return;
        }

        const command = ToggleCommands[item];
        const {state, dispatch} = this.editorView;
        command.toggle()(state, dispatch);
        this.isDropdownOpen = false;
    }

    private update = (view: EditorView) => {
        const {state} = view;
        this.activeItems = [];
        this.disabledItems = [];

        if (!this.items) {
            return;
        }

        this.items.forEach((item: any) => {
            const command = ToggleCommands[item];
            const isActive = command.isActive(state);

            if (isActive) {
                this.activeItems.push(item);
            }

            if (!command.canExecute(state)) {
                this.disabledItems.push(item);
            }
        });

        if (this.activeItems.length === 1) {
            this.activeItem = this.activeItems[0];
        } else {
            this.activeItem = null;
        }
    }

    /**
     * Angular life cycle.
     */
    ngOnInit(): void {
        this.editorView = this.menuService.editor.view;

        this.updateSubscription = this.menuService.editor.update.subscribe((view: EditorView) => {
            this.update(view);
        });
    }

    /**
     * Angular life cycle.
     */
    ngOnDestroy(): void {
        this.updateSubscription.unsubscribe();
    }
}
